import EmailIcon from '@mui/icons-material/Email';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import PhoneIcon from '@mui/icons-material/Phone';
import { Box, Button, Divider, Grid, Stack, SvgIcon, Typography } from '@mui/material';
import {
    ArrayField,
    DateField,
    DeleteButton,
    EditButton,
    EmailField,
    FunctionField,
    ReferenceField,
    ReferenceManyField,
    SelectField,
    ShowButton,
    SingleFieldList,
    TextField,
    UrlField,
    useRecordContext,
    WithRecord,
} from 'react-admin';
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";

import { useLocation } from 'react-router';
import { useConfigurationContext } from '../../root/ConfigurationContext';
import { Transaction, User } from '../../types/types';
import { ReactNode } from 'react';

const formatDate = (dateStr: string | undefined) =>
    dateStr ? new Date(dateStr).toUTCString() : "N/A";

function formatTimestamp(timestamp: number | string | Date): string {
    const date = new Date(timestamp);

    const day: string = String(date.getDate()).padStart(2, '0');
    const month: string = String(date.getMonth() + 1).padStart(2, '0');
    const year: string = String(date.getFullYear()).slice(-2);

    const hours: string = String(date.getHours()).padStart(2, '0');
    const minutes: string = String(date.getMinutes()).padStart(2, '0');
    const seconds: string = String(date.getSeconds()).padStart(2, '0');

    return `${day}.${month}.${year} - ${hours}:${minutes}:${seconds}`;
}

export const TransactionAside = ({ link = 'edit' }: { link?: 'edit' | 'show' }) => {
    const location = useLocation();
    const record = useRecordContext<Transaction>();

    if (!record) return null;

    // Status Icons
    const statusIcons = {
        pending: <HourglassTopIcon sx={{ verticalAlign: "middle" }} color="primary" />,
        completed: <CheckCircleIcon sx={{ verticalAlign: "middle" }} color="success" />,
        canceled: <CancelIcon sx={{ verticalAlign: "middle" }} color="error" />
    };

    const handleCancelTransaction = () => {
        console.log("Canceling transaction:", record.id);
        // Implement transaction cancellation logic here
    };

    const handleGeneratePDF = () => {
        console.log("Generating PDF statement for transaction:", record.id);
        // Implement PDF generation logic here
    };

    const capitalizeFirstLetter = (str: string) =>
        str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

    return (
        <Box ml={4} width={250} minWidth={250}>
            <Stack spacing={2}>
                <Box display="flex" alignItems="center">
                    <Typography variant="body2" ><strong>Status:</strong> {capitalizeFirstLetter(record.status)}</Typography>
                </Box>


                {/* Completed or Canceled Transactions */}

                <>
                    <Grid container spacing={0}>
                        <Grid item xs={2}>
                            <Typography variant="body2" color="text.secondary"><strong>Created:</strong></Typography>
                        </Grid>
                        <Grid item xs={10} sx={{ textAlign: "right" }}>
                            <Typography variant="body2" color="text.secondary">{formatTimestamp(record.timestamp)}</Typography>
                        </Grid>

                        {record.status === "completed" && (
                            <>
                                <Grid item xs={2}>
                                    <Typography variant="body2" color="text.secondary"><strong>Payment:</strong></Typography>
                                </Grid>
                                <Grid item xs={10} sx={{ textAlign: "right" }}>
                                    <Typography variant="body2" color="text.secondary">{formatTimestamp(record.timestamp)}</Typography>
                                </Grid>

                                <Grid item xs={2}>
                                    <Typography variant="body2" color="text.secondary"><strong>Statement:</strong></Typography>
                                </Grid>
                                <Grid item xs={10} sx={{ textAlign: "right" }}>
                                    <Typography variant="body2" color="text.secondary">{formatTimestamp(record.timestamp)}</Typography>
                                </Grid>
                            </>
                        )}

                        {record.status === "canceled" && (
                            <>
                                <Grid item xs={2}>
                                    <Typography variant="body2" color="text.secondary"><strong>Canceled:</strong></Typography>
                                </Grid>
                                <Grid item xs={10} sx={{ textAlign: "right" }}>
                                    <Typography variant="body2" color="text.secondary">{formatTimestamp(record.timestamp)}</Typography>
                                </Grid>
                            </>
                        )}
                    </Grid>
                </>


                <Divider sx={{ mb: 2 }} />

                {record.status === "pending" && (
                    <>
                        <Button variant="contained" color="primary" onClick={handleCancelTransaction}>
                            Cancel
                        </Button>
                    </>
                )}

                <Button variant="contained" color="primary" onClick={handleGeneratePDF}>
                    PDF
                </Button>

            </Stack>
        </Box>
    );
};

const PersonalInfoRow = ({
    icon,
    primary,
    showType,
}: {
    icon: ReactNode;
    primary: ReactNode;
    showType?: boolean;
}) => (
    <Stack direction="row" alignItems="center" gap={1} minHeight={24}>
        {icon}
        <Box display="flex" flexWrap="wrap" columnGap={0.5} rowGap={0}>
            {primary}
            {showType ? (
                <WithRecord
                    render={row =>
                        row.type !== 'Other' && (
                            <TextField source="type" color="textSecondary" />
                        )
                    }
                />
            ) : null}
        </Box>
    </Stack>
);
